import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/Navbar';
import { ChevronRight } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/use-mobile';
import AgendaSidebar from '@/components/AgendaSidebar';
import EventDetailModal from '@/components/agenda/EventDetailModal';
import StudentEventDetailModal from '@/components/agenda/StudentEventDetailModal';
import EventsActionsModal from '@/components/agenda/EventsActionsModal';
import ExportEventsModal from '@/components/agenda/ExportEventsModal';
import CopyEventsModal from '@/components/agenda/CopyEventsModal';
import { useLanguage } from '@/contexts/LanguageContext'; 
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import CalendarDisplay from '@/components/agenda/CalendarDisplay';
import AgendaHeader from '@/components/agenda/AgendaHeader';
import NewLessonDialog from '@/components/agenda/NewLessonDialog';
import NewLessonDialogSchool from '@/components/agenda/NewLessonDialogSchool';
import NewLessonDialogStudent from '@/components/agenda/NewLessonDialogStudent';
import AgendaWizard from '@/components/agenda/AgendaWizard';
import { startOfDay, startOfYear, endOfYear, startOfMonth, endOfMonth } from 'date-fns';
import SpotlightWizard from '@/components/SpotlightWizard';
import { eventService, CalendarEvent, getTeacherPrices } from '@/services/eventService';
import { formatInTimeZone } from 'date-fns-tz';
import { BASE_URL } from '@/config/api';

const TIMEZONE_STORAGE_KEY = 'calendar-timezone';
const FIRST_DAY_STORAGE_KEY = 'calendar-first-day';
const SLOT_MIN_TIME_STORAGE_KEY = 'calendar-slot-min-time';
const SLOT_MAX_TIME_STORAGE_KEY = 'calendar-slot-max-time';
const EVENT_TEXT_COLOR_STORAGE_KEY = 'calendar-event-text-color';

const Agenda = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const [showLessonWizard, setShowLessonWizard] = useState(false);
  const lessonDialogWasOpenRef = useRef(false);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['categories', token] });
    queryClient.invalidateQueries({ queryKey: ['students', token] });
    queryClient.invalidateQueries({ queryKey: ['teachers', token] });
    queryClient.invalidateQueries({ queryKey: ['locations', token] });
  }, [token, queryClient]);

  const [showNewLessonDialog, setShowNewLessonDialog] = useState(false);
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const savedCurrentDate = localStorage.getItem('agenda-current-date');
    const savedCheckDate = localStorage.getItem('agenda-check-date');
    const currentDateNow = new Date();
    if (savedCurrentDate && (user.account_type === "C" || user.account_type === "S")) {
      
      if (savedCheckDate) {
        const parsedCheckDate = new Date(savedCheckDate);
        
        if (isNaN(parsedCheckDate.getTime())) {
            return currentDateNow;
        }
        
        const timeDifferenceMs = currentDateNow.getTime() - parsedCheckDate.getTime();
        const THRESHOLD_MS = 15 * 60 * 1000; 

        if (timeDifferenceMs > THRESHOLD_MS) {
          return currentDateNow; 
        }
      }

      const parsedCurrentDate = new Date(savedCurrentDate);
      
      if (isNaN(parsedCurrentDate.getTime())) {
          return currentDateNow;
      }
      
      return parsedCurrentDate; 
    }
    
    return currentDateNow;
  });
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [showStudentEventDetailModal, setShowStudentEventDetailModal] = useState(false);
  

  const [isAllDay, setIsAllDay] = useState(false);
  const [currentView, setCurrentView] = useState(() => {
    if (user.account_type === 'Student') {
      return 'listWeek';
    }
    return isMobile ? 'timeGridDay' : 'timeGridWeek';
  });
  const [showMarkCompletedModal, setShowMarkCompletedModal] = useState(false);
  const [showDeleteEventsModal, setShowDeleteEventsModal] = useState(false);
  const [showExportEventsModal, setShowExportEventsModal] = useState(false);
  const [showCopyEventsModal, setShowCopyEventsModal] = useState(false);
  const [calendarKey, setCalendarKey] = useState(Date.now());
  const [viewDateRange, setViewDateRange] = useState<{start: string, end: string} | null>(null);
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [loadedFrom, setLoadedFrom] = useState<Date | null>(null);
  const [loadedUntil, setLoadedUntil] = useState<Date | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showWeekends, setShowWeekends] = useState(true);

  const [schoolStudents, setSchoolStudents] = useState<any[]>([]);


  const [firstDay, setFirstDay] = useState(() => {
    const savedFirstDay = localStorage.getItem(FIRST_DAY_STORAGE_KEY);
    return savedFirstDay ? parseInt(savedFirstDay, 10) : 1;
  });

  const [slotMinTime, setSlotMinTime] = useState(() => {
    const savedMinTime = localStorage.getItem(SLOT_MIN_TIME_STORAGE_KEY);
    return savedMinTime || '00:00';
  });

  const [slotMaxTime, setSlotMaxTime] = useState(() => {
    const savedMaxTime = localStorage.getItem(SLOT_MAX_TIME_STORAGE_KEY);
    return savedMaxTime || '23:59';
  });

  const [eventTextColor, setEventTextColor] = useState(() => {
    const savedTextColor = localStorage.getItem(EVENT_TEXT_COLOR_STORAGE_KEY);
    return savedTextColor || 'dark';
  });

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState(() => {
    const savedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    return savedTimezone || browserTimezone;
  });
  const teacherPrices = getTeacherPrices();

  const [newLesson, setNewLesson] = useState({
    title: '', studentIds: [] as string[], categoryId: '', locationId: '',
    teacherId: [] as string[], startDate: '', endDate: '', startTime: '',
    endTime: '', isRecurring: false, recurrenceEndDate: '', note: '',
    formType: 'lesson', price: 0, price2: 0, extra: '0',
  });

  const { toast } = useToast();
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<FullCalendar>(null);
  const cachedEvents = eventService.getCachedEvents();

  // Vues "longues" qui bénéficient du chargement progressif par mois (List Année uniquement)
  const isLongRangeView = currentView === 'listYear';

  // Effect to manage loadedUntil state based on currentView / viewDateRange
  useEffect(() => {
    if (isLongRangeView && viewDateRange) {
      const viewStart = new Date(viewDateRange.start);
      const viewEnd = new Date(viewDateRange.end);
      const today = startOfDay(new Date());
      const initialStart = today > viewStart ? today : viewStart;

      setLoadedFrom(initialStart);
      const initialUntil = new Date(initialStart);
      // Charger 3 mois par défaut pour avoir une vision plus large dès l'arrivée
      initialUntil.setMonth(initialUntil.getMonth() + 3);
      setLoadedUntil(initialUntil < viewEnd ? initialUntil : viewEnd);
    } else {
      setLoadedUntil(null);
      setLoadedFrom(null);
    }
  }, [currentView, isLongRangeView, viewDateRange?.start, viewDateRange?.end]);

  // Function to extend the loadedUntil range (1 mois supplémentaire)
  const handleLoadMoreDates = useCallback(() => {
    setLoadedUntil(prev => {
      if (!prev || !viewDateRange) return prev;
      const actualViewEnd = new Date(viewDateRange.end);
      if (prev >= actualViewEnd) return prev;
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate < actualViewEnd ? newDate : actualViewEnd;
    });
  }, [viewDateRange?.end]);

  const effectiveStart = isLongRangeView
    ? loadedFrom ? format(loadedFrom, 'yyyy-MM-dd') : null
    : viewDateRange?.start;
  const effectiveEnd = isLongRangeView
    ? loadedUntil ? format(loadedUntil, 'yyyy-MM-dd') : null
    : viewDateRange?.end;

  const { data: events = [], isLoading: eventsLoading, isFetching: eventsFetching, refetch: refetchEvents } = useQuery({
    queryKey: ['events', token, effectiveStart, effectiveEnd],
    queryFn: async () => {
      if (!viewDateRange || !effectiveStart || !effectiveEnd) {
        return cachedEvents;
      }
      return await eventService.getEvents(token, effectiveStart, effectiveEnd);
    },
    initialData: cachedEvents.length > 0 ? cachedEvents : undefined,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!viewDateRange && !!effectiveStart && !!effectiveEnd,
  });

  // Auto-extend la plage si on a peu d'évènements (vue Liste Année)
  useEffect(() => {
    if (!isLongRangeView) return;
    if (eventsLoading || eventsFetching) return;
    if (!loadedUntil || !viewDateRange) return;
    const viewEnd = new Date(viewDateRange.end);
    if (loadedUntil >= viewEnd) return;
    if (events.length < 20) {
      const newDate = new Date(loadedUntil);
      newDate.setMonth(newDate.getMonth() + 2);
      setLoadedUntil(newDate < viewEnd ? newDate : viewEnd);
    }
  }, [events.length, eventsLoading, eventsFetching, isLongRangeView, loadedUntil, viewDateRange?.end]);

  const { data: students = [], isLoading: studentsLoading, refetch: refetchStudents } = useQuery({
    queryKey: ['students', token],
    queryFn: () => eventService.getAgendaStudentsList(user, token),
    staleTime: Infinity,
  });

  const { data: teachers = [], isLoading: teachersLoading, refetch: refetchTeachers } = useQuery({
    queryKey: ['teachers', token],
    queryFn: () => eventService.getTeachers(user, token),
    staleTime: Infinity,
  });

  const { data: categories = [], isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['categories', token],
    queryFn: () => eventService.getCategories(user, token),
    staleTime: Infinity,
  });

  const { data: locations = [], isLoading: locationsLoading, refetch: refetchLocations } = useQuery({
    queryKey: ['locations', token],
    queryFn: () => eventService.getLocations(user, token),
    staleTime: Infinity,
  });

  const [filters, setFilters] = useState({
    students: [] as string[],
    teachers: [] as string[],
    categories: [] as string[],
    locations: [] as string[],
    schools: [] as string[],
    showCompleted: true,
  });

  useEffect(() => {
    if (!calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();
    const handleFullCalendarDatesSet = (dateInfo: any) => {
      const start = format(dateInfo.start, 'yyyy-MM-dd');
      const end = format(dateInfo.end, 'yyyy-MM-dd');
      setViewDateRange({ start, end });
      const currentDate = calendarApi.getDate();
      if (currentDate) {
        setSelectedDate(currentDate);
        const currentDateNow = new Date();
        localStorage.setItem('agenda-check-date', currentDateNow.toISOString());
        localStorage.setItem('agenda-current-date', currentDate.toISOString());
      }
    };
    calendarApi.on('datesSet', handleFullCalendarDatesSet);
    return () => {
      calendarApi.off('datesSet', handleFullCalendarDatesSet);
    };
  }, [calendarRef.current]);

  useEffect(() => {
  if (calendarRef.current && selectedDate) {
    const calendarApi = calendarRef.current.getApi();
    const currentDate = calendarApi.getDate();
    if (currentDate && currentDate.getTime() !== selectedDate.getTime()) {
      setTimeout(() => {
        calendarApi.gotoDate(selectedDate);
      }, 0);
    }
  }
}, [calendarRef.current, selectedDate]);



  /*useEffect(() => {
  if (!calendarRef.current || !selectedDate) return;

  const calendarApi = calendarRef.current.getApi();
  if (!calendarApi) return;

  // Évite de rappeler `gotoDate` si la date est déjà celle du calendrier
  const currentDate = calendarApi.getDate();
  if (currentDate && currentDate.getTime() !== selectedDate.getTime()) {
    calendarApi.gotoDate(selectedDate);
  }
}, [selectedDate]);*/


  const getFullCalendarEvents = () => {
    return eventService.convertEvents(events);
  };

  const handleDateClick = (arg: any) => {
    let startDate = new Date(arg.date);
    const selectedDate = new Date(arg.date);
    const dateStr = format(startDate, 'yyyy-MM-dd');

    const formatTime = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const startTime = formatTime(startDate);
    const endTime = formatTime(new Date(startDate.getTime() + 15 * 60 * 1000));
    const isAllDay = arg.allDay === true;

    setNewLesson(prev => ({
      ...prev,
      title: '',
      studentIds: [] as string[],
      categoryId: '',
      teacherId: [] as string[],
      locationId: '',
      startDate: dateStr,
      endDate: dateStr,
      recurrenceEndDate: dateStr,
      isRecurring: false,
      startTime: isAllDay ? '00:00:01' : startTime,
      endTime: isAllDay ? '23:59:59' : endTime,
      note: '',
      formType: isAllDay ? 'event' : 'lesson'
    }));

    setIsAllDay(isAllDay);
    setShowNewLessonDialog(true);
  };

  const handleSelect = (arg: any) => {
    
    let startDate = new Date(arg.start);
    let endDate = new Date(arg.end);

    // Détecte si la sélection est all-day ou couvre plusieurs jours
    const isAllDaySlot = arg.allDay || startDate.toDateString() !== endDate.toDateString();

    if (isAllDaySlot) {
      // Normalise les heures à minuit
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // Si ce n’est PAS déjà un allDay fourni par FullCalendar,
      // (c’est-à-dire une sélection horaire sur plusieurs jours),
      // alors on ajoute 1 jour pour inclure le dernier jour sélectionné
      if (!arg.allDay && startDate.getTime() !== endDate.getTime()) {
        endDate.setDate(endDate.getDate() + 1);
      }
    }

    setSelectedEvent(arg);
    setSelectedDate(startDate);
    setIsAllDay(isAllDaySlot);

    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');

    const formatTime = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);

    setNewLesson(prev => ({
      ...prev,
      title: '',
      studentIds: [] as string[],
      categoryId: '',
      teacherId: [] as string[],
      locationId: '',
      startDate: startDateStr,
      endDate: endDateStr,
      recurrenceEndDate: endDateStr,
      isRecurring: false,
      startTime: startTime,
      endTime: endTime,
      note: '',
      formType: isAllDaySlot ? 'event' : 'lesson'
    }));

    setShowNewLessonDialog(true);
  };

  const handleEventClick = async (info: any) => {
  if (user?.account_type === "Student" || user?.account_type === "Parent") {
    setSelectedEvent(info.event);
    setShowStudentEventDetailModal(true);
  } else {
    setSelectedEvent(info.event);
    // Si l'événement est de type "S", on récupère les étudiants de l'école
    if (info.event.extendedProps?.invoice_type === "S" && user?.roleType === "teachers_all") {
      try {
          const schoolId = Number(info.event.extendedProps.laschool);
          const response = await fetch(`${BASE_URL}/api/students/school/${schoolId}`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            setSchoolStudents(data.students || []);
          } else {
            toast({
              title: "Failed to fetch school students",
              description: "There was an error loading the school students list.",
              variant: "destructive"
            });
          }
      } catch (error) {
        console.error('Error fetching school students:', error);
        toast({
          title: "Failed to fetch school students",
          description: "There was an error loading the school students list.",
          variant: "destructive"
        });
      }
    } else {
      // Réinitialise la liste des étudiants de l'école si ce n'est pas un événement "S"
      setSchoolStudents([]);
    }
    setShowEventDetailModal(true);
  }
};


  const handleCreateLesson = async () => {
    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  const handleMarkComplete = (eventIds: string | string[]) => {
    const ids = Array.isArray(eventIds) ? eventIds : [eventIds];

    toast({
      title: "Lessons marked as completed",
      description: `${ids.length} lessons have been marked as completed`,
    });

    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  const handleUpdateEvent = (eventData: any) => {
    toast({
      title: "Lesson updated",
      description: "The lesson details have been updated",
    });

    queryClient.invalidateQueries({ queryKey: ['events'] });

    setShowEventDetailModal(false);
  };

  const handleDeleteEvents = (eventIds: string[]) => {
    toast({
      title: "Events deleted",
      description: `${eventIds.length} events have been deleted`,
    });

    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  const handleExportEvents = () => {
    setShowExportEventsModal(true);
  };

  const getEventsInCurrentView = (): CalendarEvent[] => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      const view = calendarApi.view;
      const start = view.activeStart;
      const end = view.activeEnd;

      return events.filter((event: CalendarEvent) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        return eventStart < end && eventEnd >= start;
      });
    }

    return events;
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.gotoDate(date);
      }
      localStorage.setItem('agenda-current-date', date.toISOString());
      const currentDateNow = new Date();
      const userTimezone = user.timezone || 'UTC';
      const formattedDate = formatInTimeZone(currentDateNow, userTimezone, 'yyyy-MM-dd HH:mm:ss');
      localStorage.setItem('agenda-check-date', formattedDate);
    }
  };

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleToggleView = (view: string) => {
    setCurrentView(view);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      if (view === 'listView') {
        calendarApi.changeView('listWeek');
      } else {
        calendarApi.changeView(view);
      }

      const start = format(calendarApi.view.activeStart, 'yyyy-MM-dd');
      const end = format(calendarApi.view.activeEnd, 'yyyy-MM-dd');
      setViewDateRange({ start, end });
    }
  };

  const handlePrevNext = (direction: 'prev' | 'next') => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      if (direction === 'prev') {
        calendarApi.prev();
      } else {
        calendarApi.next();
      }
      setSelectedDate(calendarApi.getDate());

      const start = format(calendarApi.view.activeStart, 'yyyy-MM-dd');
      const end = format(calendarApi.view.activeEnd, 'yyyy-MM-dd');
      setViewDateRange({ start, end });
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setSelectedDate(calendarApi.getDate());

      const start = format(calendarApi.view.activeStart, 'yyyy-MM-dd');
      const end = format(calendarApi.view.activeEnd, 'yyyy-MM-dd');
      setViewDateRange({ start, end });
    }
  };

  const handleYearChange = (delta: number) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const currentDate = calendarApi.getDate();
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + delta);
    calendarApi.gotoDate(newDate);
    setSelectedDate(newDate);
  };

  const handleStudentAdded = async (firstname: string, lastname: string, id: any) => {
    try {
      const fullName = `${firstname} ${lastname}`;

      await refetchStudents();

      setNewLesson(prev => ({
        ...prev,
        studentIds: [...prev.studentIds, id]
      }));

      setShowAddStudentForm(false);

      toast({
        title: t('agenda.studentAdded'),
        description: `${fullName} ${t('agenda.addedToStudentsList')}`
      });
    } catch (error) {
      console.error("Error adding student:", error);
      toast({
        title: t('agenda.errorAddingStudent'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const handleCategoryAdded = async (name: string, color: string, price: number, newCategoryId: any) => {
    try {
      await refetchCategories();
      const addedCategory = categories.find((c: any) => c.id === newCategoryId);
      if (addedCategory) {
          setNewLesson(prev => ({
            ...prev,
            categoryId: addedCategory.id
          }));
      }

      setShowAddCategoryForm(false);

      toast({
        title: t('agenda.categoryAdded'),
        description: `${name} ${t('agenda.addedToCategoriesList')}`
      });
    } catch (error) {
      console.error("Error adding category:", error);
      toast({
        title: t('agenda.errorAddingCategory'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const handleLocationAdded = async (name: string) => {
    try {
      const response = await eventService.addLocation(user, token, { name });

      await refetchLocations();

      const newLocations = await eventService.getLocations(user, token);

      const addedLocation = newLocations.find((l: any) => l.name === name);

      if (addedLocation) {
          setNewLesson(prev => ({
            ...prev,
            locationId: addedLocation.id
          }));
      }

      setShowAddLocationForm(false);

      toast({
        title: t('agenda.locationAdded'),
        description: `${name} ${t('agenda.addedToLocationsList')}`
      });
    } catch (error) {
      console.error("Error adding location:", error);
      toast({
        title: t('agenda.errorAddingLocation'),
        description: t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    }
  };

  const getEventsCountInView = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const view = calendarApi.view;
      const start = view.activeStart;
      const end = view.activeEnd;

      const visibleEvents = events.filter((event: CalendarEvent) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        return eventStart < end && eventEnd >= start;
      });

      return visibleEvents.length;
    }
    return 0;
  };

  const handleDatesSet = (dateInfo: any) => {
    const start = format(dateInfo.start, 'yyyy-MM-dd');
    const end = format(dateInfo.end, 'yyyy-MM-dd');

    if (!viewDateRange || viewDateRange.start !== start || viewDateRange.end !== end) {
      setViewDateRange({ start, end });
    }

    if (dateInfo.view.type !== currentView) {
      setCurrentView(dateInfo.view.type);
    }
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const handleViewChange = () => {
        setCurrentView(calendarApi.view.type);
      };

      calendarApi.on('datesSet', handleViewChange);

      return () => {
        calendarApi.off('datesSet', handleViewChange);
      };
    }
  }, []);

  const openNewLessonDialog = () => {
    const timezone = user.timezone;
    const now = new Date();
    const roundedMinutes = Math.ceil(now.getMinutes() / 5) * 5;
    const startTime = new Date(now);
    startTime.setMinutes(roundedMinutes, 0, 0);
    const endTime = new Date(startTime.getTime() + 60 * 60000);
    const formatDate = (date) => {
      return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
    };
    const formatTime = (date) => {
      return formatInTimeZone(date, timezone, 'HH:mm');
    };
    const defaultStartDate = formatDate(now);
    const defaultEndDate = formatDate(now);
    const defaultStartTime = formatTime(startTime);
    const defaultEndTime = formatTime(endTime);
    setNewLesson(prev => ({
      ...prev,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
    }));
    setIsAllDay(false);
    setShowNewLessonDialog(true);
  };

  // Activate lesson wizard when arriving with ?wizard=lesson or wizard-step=4
  useEffect(() => {
    if (user?.is_wizard_disable) return;
    const params = new URLSearchParams(location.search);
    const wizardActive = localStorage.getItem('wizard-active') === 'true';
    const wizardStep = localStorage.getItem('wizard-step');
    if (params.get('wizard') === 'lesson' || (wizardActive && wizardStep === '4')) {
      setShowLessonWizard(true);
      if (params.get('wizard') === 'lesson') {
        params.delete('wizard');
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If wizard is stopped externally (X button confirm), hide the lesson spotlight
  useEffect(() => {
    const onWizardChange = () => {
      const active = localStorage.getItem('wizard-active') === 'true';
      if (!active) {
        setShowLessonWizard(false);
        lessonDialogWasOpenRef.current = false;
      }
    };
    window.addEventListener('wizard-state-changed', onWizardChange);
    return () => window.removeEventListener('wizard-state-changed', onWizardChange);
  }, []);

  // Close wizard when lesson dialog opens then closes (creation done or canceled)
  useEffect(() => {
    if (!showLessonWizard) return;
    if (showNewLessonDialog) {
      lessonDialogWasOpenRef.current = true;
    } else if (lessonDialogWasOpenRef.current) {
      setShowLessonWizard(false);
      localStorage.setItem('wizard-active', 'false');
      localStorage.setItem('wizard-step', 'done');
      window.dispatchEvent(new Event('wizard-state-changed'));
      lessonDialogWasOpenRef.current = false;
    }
  }, [showNewLessonDialog, showLessonWizard]);

  const closeLessonWizard = () => {
    setShowLessonWizard(false);
    localStorage.setItem('wizard-active', 'false');
    localStorage.setItem('wizard-step', 'done');
    window.dispatchEvent(new Event('wizard-state-changed'));
  };

  const toggleSidebarVisibility = () => {
    setIsSidebarHidden(!isSidebarHidden);
    if (isMobile) {
      setSidebarExpanded(false);
    } else {
      setSidebarExpanded(isSidebarHidden);
    }
  };

  const createDateInTimezone = (timezone: string): Date => {
    const now = new Date();

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false
    });

    const parts = formatter.formatToParts(now);

    const dateParts: Record<string, number> = {};
    parts.forEach(part => {
      if (part.type !== 'literal') {
        dateParts[part.type] = parseInt(part.value, 10);
      }
    });

    const dateInTimezone = new Date(
      dateParts.year,
      dateParts.month - 1,
      dateParts.day,
      dateParts.hour,
      dateParts.minute,
      dateParts.second
    );

    return dateInTimezone;
  };

  const getCurrentTimeInTimezone = (tz: string) => {
    const formatter = new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    return formatter.format(new Date());
  };

  const formatCurrentTimezoneTime = () => {
    return getCurrentTimeInTimezone(timezone);
  };

  const [currentNow, setCurrentNow] = useState<Date | null>(null);

  useEffect(() => {
    if (timezone !== browserTimezone) {
      setCurrentNow(createDateInTimezone(timezone));
    } else {
      setCurrentNow(null);
    }

    const timer = setInterval(() => {
      if (timezone !== browserTimezone) {
        setCurrentNow(createDateInTimezone(timezone));
      }
    }, 60000);

    return () => clearInterval(timer);
  }, [timezone, browserTimezone]);

  const resetCalendar = () => {
    setCalendarKey(Date.now());
    toast({
      title: t('agenda.resetCalendar'),
      description: timezone,
    });
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setTimezone(newTimezone);

    localStorage.setItem(TIMEZONE_STORAGE_KEY, newTimezone);

    if (newTimezone === browserTimezone) {
      setCurrentNow(null);
    } else {
      setCurrentNow(createDateInTimezone(newTimezone));
    }

    resetCalendar();

    toast({
      title: t('agenda.timezoneChanged'),
      description: `${t('agenda.calendarNowUsing')} ${newTimezone}`,
    });
  };

  const scrollToCurrentTime = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi && (currentView === 'timeGridWeek' || currentView === 'timeGridDay')) {
      const container = document.querySelector('.fc-timegrid-body');
      const nowIndicator = container?.querySelector('.fc-timegrid-now-indicator-line');

      if (nowIndicator && container) {
        nowIndicator.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToCurrentTime();
    }, 500);
    return () => clearTimeout(timer);
  }, [calendarKey, currentView]);

  const toggleWeekends = () => {
    const newShowWeekends = !showWeekends;
    setShowWeekends(newShowWeekends);

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.setOption('weekends', newShowWeekends);
      toast({
        title: newShowWeekends ? t('agenda.weekendsShown') : t('agenda.weekendsHidden'),
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const getEventWithDateObjects = (events: CalendarEvent[]) => {
    return events.map(event => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end)
    }));
  };

  const handleEventRefresh = async () => {
    eventService.clearEventsCache();
    const result = await refetchEvents();
    const refreshedEvent = result.data?.find(
      (item: CalendarEvent) => String(item.id) === String(selectedEvent?.id)
    );

    if (refreshedEvent) {
      setSelectedEvent(eventService.convertToFullCalendarEvent(refreshedEvent));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobileNow = window.innerWidth < 768;
      if (isMobileNow && currentView === 'timeGridWeek') {
        setCurrentView('timeGridDay');
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.changeView('timeGridDay');
        }
      } else if (!isMobileNow && currentView === 'timeGridDay') {
        setCurrentView('timeGridWeek');
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.changeView('timeGridWeek');
        }
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [showWizard, setShowWizard] = useState(false);
  const [wizardTargetDate, setWizardTargetDate] = useState<Date>(new Date());

  useEffect(() => {
      if (getEventsCountInView() === 0 && !user.hasCreatedEvent && user.is_active) {
        setWizardTargetDate(new Date());
        setShowWizard(false);
      } else {
        setShowWizard(false);
      }
  }, [getEventsCountInView()]);

  const handleWizardDateClick = () => {
    localStorage.setItem('agenda-wizard-shown', 'true');

    const today = new Date();
    const dateStr = format(today, 'yyyy-MM-dd');
    const defaultStartTime = '10:00';
    const defaultEndTime = '11:00';

    setNewLesson(prev => ({
      ...prev,
      title: '',
      studentIds: [] as string[],
      categoryId: '',
      teacherId: [] as string[],
      locationId: '',
      startDate: dateStr,
      endDate: dateStr,
      isRecurring: false,
      recurrenceEndDate: dateStr,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      note: '',
      formType: 'lesson'
    }));

    setIsAllDay(false);
    setSelectedDate(today);
    setShowWizard(false);
    setShowNewLessonDialog(true);
  };

  const handleFirstDayChange = (day: number) => {
    setFirstDay(day);
    localStorage.setItem(FIRST_DAY_STORAGE_KEY, day.toString());
    setCalendarKey(Date.now());

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.setOption('firstDay', day);
    }
  };

  const handleSlotMinTimeChange = (time: string) => {
    setSlotMinTime(time);
    localStorage.setItem(SLOT_MIN_TIME_STORAGE_KEY, time);

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.setOption('slotMinTime', time);
    }
  };

  const handleSlotMaxTimeChange = (time: string) => {
    setSlotMaxTime(time);
    localStorage.setItem(SLOT_MAX_TIME_STORAGE_KEY, time);

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.setOption('slotMaxTime', time);
    }
  };

  const handleEventTextColorChange = (color: string) => {
    const textColor = color === 'dark' ? '#333333' : '#FFFFFF';
    setEventTextColor(color);
    localStorage.setItem(EVENT_TEXT_COLOR_STORAGE_KEY, color);

    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.setOption('eventTextColor', textColor);
    }
  };

  const handleFiltersChange = (newFilters: any) => {
    //console.log('Filters changed in Agenda:', newFilters);
    setFilters(newFilters);
  };

  const handleCopyEvents = () => {
    setShowCopyEventsModal(true);
  };

  return (
    <div className={`flex flex-col min-h-screen overflow-y-auto ${showWizard ? 'wizard-active' : ''}`} ref={mainContainerRef}>
      <Navbar />

      <main className="flex-1 container py-4 md:py-8 mt-16 overflow-y-auto">
        <AgendaHeader
          isSidebarHidden={isSidebarHidden}
          toggleSidebarVisibility={toggleSidebarVisibility}
          timezone={timezone}
          showWeekends={showWeekends}
          toggleWeekends={toggleWeekends}
          resetCalendar={resetCalendar}
          eventsInView={getEventsCountInView()}
          formatCurrentTimezoneTime={formatCurrentTimezoneTime}
          firstDay={firstDay}
          handleFirstDayChange={handleFirstDayChange}
          slotMinTime={slotMinTime}
          handleSlotMinTimeChange={handleSlotMinTimeChange}
          slotMaxTime={slotMaxTime}
          handleSlotMaxTimeChange={handleSlotMaxTimeChange}
          eventTextColor={eventTextColor}
          handleEventTextColorChange={handleEventTextColorChange}
          currentView={currentView}
          viewDateRange={viewDateRange}
          onYearChange={handleYearChange}
        />
        <div className={`flex flex-col md:gap-4 h-[calc(100vh-14rem)] ${isMobile ? 'agenda-mobile-layout' : ''}`}>
          <div className={`md:flex h-full ${isMobile ? 'flex-col' : 'flex-row'}`}>
            {(sidebarExpanded && !isMobile || isMobile && sidebarExpanded) && (
              <div className={`${isMobile ? 'w-full mb-4' : 'w-80 mr-4'} h-full`}>
                <AgendaSidebar
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  isExpanded={sidebarExpanded}
                  onToggle={toggleSidebar}
                  onToggleView={handleToggleView}
                  onPrevNext={handlePrevNext}
                  onToday={handleToday}
                  currentView={currentView}
                  onNewLesson={openNewLessonDialog}
                  onMarkCompleted={() => setShowMarkCompletedModal(true)}
                  onDeleteEvents={() => setShowDeleteEventsModal(true)}
                  onExportEvents={handleExportEvents}
                  onCopyEvents={handleCopyEvents}
                   showNewLessonDialog={showNewLessonDialog}
                  setShowNewLessonDialog={setShowNewLessonDialog}
                  timezone={timezone}
                  onFiltersChange={handleFiltersChange}
                  events={events}
                  firstDay={firstDay}
                  onRefreshEvents={handleEventRefresh}
                />
              </div>
            )}

            <div className={`relative flex-1 ${isMobile ? 'mb-4 order-1' : ''}`}>
              <CalendarDisplay
                calendarKey={calendarKey}
                currentView={currentView}
                events={getFullCalendarEvents()}
                timezone={timezone}
                currentNow={currentNow}
                browserTimezone={browserTimezone}
                showWeekends={showWeekends}
                sidebarExpanded={sidebarExpanded}
                isMobile={isMobile}
                onDateClick={handleDateClick}
                onSelect={handleSelect}
                onEventClick={handleEventClick}
                toggleSidebar={toggleSidebar}
                calendarRef={calendarRef}
                onRefreshEvents={handleEventRefresh}
                isLoadingEvents={eventsLoading || eventsFetching}
                onDatesSet={handleDatesSet}
                filters={filters}
                firstDay={firstDay}
                slotMinTime={slotMinTime}
                slotMaxTime={slotMaxTime}
                eventTextColor={eventTextColor === 'dark' ? '#333333' : '#FFFFFF'}
                onLoadMoreDates={handleLoadMoreDates} // Pass the lazy load callback
                canLoadMore={isLongRangeView && !!loadedUntil && !!viewDateRange && new Date(viewDateRange.end) > loadedUntil}
              />
            </div>
          </div>
        </div>
      </main>

      <AgendaWizard
        isVisible={showWizard}
        targetDate={wizardTargetDate}
        onDateClick={handleWizardDateClick}
      />

      {/* Lesson creation wizard — guides user to create their first lesson */}
      <SpotlightWizard
        isVisible={showLessonWizard && !showNewLessonDialog}
        targetSelector="#agenda-new-lesson-btn"
        message={
          t('wizard.createLesson') ||
          "Cliquez sur \"Nouveau cours\" ici, ou cliquez directement sur une case du calendrier pour créer votre premier cours."
        }
        position={isMobile ? 'top' : 'right'}
        showCloseButton={true}
        onClose={closeLessonWizard}
      />

      {(user.account_type === 'Student' || user.account_type === 'Parent') ? (
        <NewLessonDialogStudent
          open={showNewLessonDialog}
          onOpenChange={setShowNewLessonDialog}
          onAbsenceCreated={handleCreateLesson}
        />
      ) : (user.account_type === 'C' || user.roleType === 'teachers_all') ? (
        <NewLessonDialog
          open={showNewLessonDialog}
          onOpenChange={setShowNewLessonDialog}
          newLesson={newLesson}
          setNewLesson={setNewLesson}
          isAllDay={isAllDay}
          setIsAllDay={setIsAllDay}
          students={students.filter(student => student.status !== 'deleted')}
          categories={categories}
          locations={locations}
          showAddStudentForm={showAddStudentForm}
          setShowAddStudentForm={setShowAddStudentForm}
          showAddCategoryForm={showAddCategoryForm}
          setShowAddCategoryForm={setShowAddCategoryForm}
          showAddLocationForm={showAddLocationForm}
          setShowAddLocationForm={setShowAddLocationForm}
          handleStudentAdded={handleStudentAdded}
          handleCategoryAdded={handleCategoryAdded}
          handleLocationAdded={handleLocationAdded}
          handleCreateLesson={handleCreateLesson}
        />
      ) : user.account_type === 'S' ? (
        <NewLessonDialogSchool
          open={showNewLessonDialog}
          onOpenChange={setShowNewLessonDialog}
          newLesson={newLesson}
          setNewLesson={setNewLesson}
          isAllDay={isAllDay}
          setIsAllDay={setIsAllDay}
          students={students.filter(student => student.status !== 'deleted')}
          teachers={teachers.filter(student => student.status !== 'deleted')}
          teacherPrices={teacherPrices}
          categories={categories}
          locations={locations}
          showAddStudentForm={showAddStudentForm}
          setShowAddStudentForm={setShowAddStudentForm}
          showAddCategoryForm={showAddCategoryForm}
          setShowAddCategoryForm={setShowAddCategoryForm}
          showAddLocationForm={showAddLocationForm}
          setShowAddLocationForm={setShowAddLocationForm}
          handleStudentAdded={handleStudentAdded}
          handleCategoryAdded={handleCategoryAdded}
          handleLocationAdded={handleLocationAdded}
          handleCreateLesson={handleCreateLesson}
        />
      ) : null}


      {selectedEvent && (user?.account_type === "C" || user?.account_type === "S") && (
        <EventDetailModal
          event={selectedEvent}
          teacherPrices={teacherPrices}
          events={events}
          open={showEventDetailModal}
          onOpenChange={setShowEventDetailModal}
          onUpdate={handleUpdateEvent}
          students={
            selectedEvent?.extendedProps?.invoice_type === "S" && user?.roleType === "teachers_all"
              ? schoolStudents
              : students.filter(student => student.status !== 'deleted')
          }
          teachers={teachers}
          categories={categories}
          locations={locations}
          onRefreshEvents={handleEventRefresh}
          setShowNewLessonDialog={setShowNewLessonDialog}
          setNewLesson={setNewLesson}
          handleStudentAdded={handleStudentAdded}
          handleCategoryAdded={handleCategoryAdded}
          handleLocationAdded={handleLocationAdded}
        />
      )}


    
    {selectedEvent && (user?.account_type === "Student" || user?.account_type === "Parent") && (
      <StudentEventDetailModal
          event={selectedEvent}
          events={events}
          open={showStudentEventDetailModal}
          onOpenChange={setShowStudentEventDetailModal}
          students={students}
          onRefreshEvents={handleEventRefresh}
        />
    )}


      <EventsActionsModal
        mode="complete"
        open={showMarkCompletedModal}
        onOpenChange={setShowMarkCompletedModal}
        onSubmit={handleMarkComplete}
        events={getEventWithDateObjects(events.filter(e => !e.extendedProps.completed))}
      />
      <EventsActionsModal
        mode="delete"
        open={showDeleteEventsModal}
        onOpenChange={setShowDeleteEventsModal}
        onSubmit={handleDeleteEvents}
        events={getEventWithDateObjects(events)}
      />

      <ExportEventsModal
        open={showExportEventsModal}
        onOpenChange={setShowExportEventsModal}
        events={getEventsInCurrentView()}
        currentView={currentView}
      />

      <CopyEventsModal
        open={showCopyEventsModal}
        onOpenChange={setShowCopyEventsModal}
        events={getEventsInCurrentView()}
        currentView={currentView}
        token={token}
        selectedDate={selectedDate}
      />
    </div>
  );
};

export default Agenda;
